import { IMAGES, LOCATIONS } from "../lib/assets";
import Reveal from "../components/Reveal";
import MarqueeLink from "../components/MarqueeLink";
import ImageSlider from "../components/ImageSlider";

const whyWork = [
  "Reliable and experienced trades team",
  "Consistent finishing standards",
  "Strong communication with site managers",
  "Coordination of multiple interior trades",
  "Experience working alongside builders and developers",
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#F5F5F0]">
      {/* HERO with background image */}
      <section className="relative overflow-hidden -mt-32 md:-mt-36">
        <img
          src={IMAGES.minimalLiving}
          alt="Architectural interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-44 md:pt-56 pb-32 md:pb-44">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">About</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-10 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-[22ch] leading-[1.1]">
              A single trade partner for interior completion.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* STORY + IMAGE */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <span className="eyebrow">Our Story</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl font-medium text-[#111111] tracking-tight leading-[1.2]">
                Founded to simplify interior completion for builders and developers.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-[#545454] text-base md:text-lg leading-relaxed max-w-md">
                One reliable team, one point of contact, one standard of work — from structural lining through to final surface finishes.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={180}>
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES.minimalLiving}
                  alt="Minimal architectural interior"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-32 md:py-48 bg-[#111111] text-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">Our Mission</span>
            <h2 className="mt-8 font-heading text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight max-w-3xl leading-[1.4] text-white">
              Quality workmanship.<br />
              Clear communication.<br />
              Dependable delivery.
            </h2>
          </Reveal>
        </div>
      </section>

      {/* WHERE WE WORK — 3-image slider with city captions */}
      <section className="border-t border-[#E2E2DF]" data-testid="where-we-work">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <Reveal>
            <span className="eyebrow">Where We Work</span>
          </Reveal>
        </div>
        <ImageSlider
          testid="locations-slider"
          aspect="aspect-[16/9] md:aspect-[21/9]"
          images={LOCATIONS.map((l) => l.image)}
          captions={LOCATIONS.map((l) => l.name)}
          interval={5000}
        />
      </section>

      {/* WHY WORK WITH US */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow">Why Work With Us</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] tracking-tight leading-[1.2] font-medium">
                What you can expect on every site.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <ul>
              {whyWork.map((item, i) => (
                <Reveal as="li" key={i} delay={i * 60}>
                  <div
                    className="flex items-baseline gap-8 py-7 border-b border-[#E2E2DF]"
                    data-testid={`about-why-${i}`}
                  >
                    <span className="font-heading text-xs tracking-[0.25em] text-[#EB1D25] font-semibold w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#111111] text-base md:text-lg font-heading font-normal">
                      {item}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Reveal>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-[#111111] tracking-tight font-medium leading-[1.2] max-w-2xl">
                Working on a project? We'd love to hear about it.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Reveal delay={120}>
              <MarqueeLink to="/contact" variant="ghost-dark" data-testid="about-cta-contact">
                Get in touch
              </MarqueeLink>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
